import React from 'react';

import { isMinLengthArray } from 'src/lib/isMinLengthArray';

import { useGetUsersForMentions } from './useMentionUsers';
import { UserMarkdownElement } from './UserMarkdownElement';

const userIdFullRegex = /{{\s*?user.[^ \}]+\s*?}}/;
const userIdRegex =
  /(?<delimit_start>{{\s*?)user.(?<id>[^ \}]+)(?<delimit_end>\s*?}})/gi;

const tryParseCustomComponentChild = (
  source: string,
):
  | {
      type: 'users';
      parts: Array<
        { string: string; key: string } | { user: string; key: string }
      >;
    }
  | { type: 'none' } => {
  const userIds = [...source.matchAll(userIdRegex)].flatMap(
    (userMatch) => userMatch?.groups?.id ?? [],
  );

  if (isMinLengthArray(1, userIds)) {
    return {
      type: 'users',
      parts: source.split(userIdFullRegex).flatMap((str, i, arr) => {
        const key = arr.slice(0, i + 1).join();
        const userId = userIds[i] ?? '';
        if (arr.length - 1 === i) return { string: str, key };
        return [
          { string: str, key },
          { user: userIds[i] ?? '', key: `${key}-${userId}` },
        ];
      }),
    };
  } else {
    return { type: 'none' };
  }
};

// We can't use markdown-to-jsx's built-in custom React component method
// (https://www.npmjs.com/package/markdown-to-jsx#optionsoverrides---rendering-arbitrary-react-components)
// because it requires `disableParsingRawHTML` to be false. To write custom components,
// we embed the data we need in the body of paragraph tags & parse it out
// when we render our custom versions of standard paragraph tags.

// If you want to add a custom component:
//   1. make a regex & a parser for the text you are going to turn into a custom compoenent
//   2. add a branch in the returns below to return your custom component
export const CustomComponentsOrParagraph = ({
  children,
  users,
  usersLoading,
  className,
  ...props
}: {
  children: React.ReactNode;
  users: ReturnType<typeof useGetUsersForMentions>['users'];
  usersLoading: boolean;
  className?: string;
}) => {
  const childContents = React.Children.toArray(children).flatMap<
    | {
        string: string;
        key: string;
      }
    | {
        user: string;
        key: string;
      }
    | NonNullable<React.ReactNode>,
    undefined
  >((childPart) => {
    const parsedChildPart = tryParseCustomComponentChild(
      typeof childPart === 'string' ? childPart : '',
    );

    const type = parsedChildPart.type;

    if (type === 'none') {
      return childPart;
    }
    return parsedChildPart.parts;
  });

  return (
    <p {...props} className={className}>
      {childContents.map((value) => {
        if (typeof value === 'object' && 'user' in value) {
          return (
            <UserMarkdownElement
              key={value.key}
              id={value.user}
              users={users}
              loading={usersLoading}
            />
          );
        } else if (typeof value === 'object' && 'string' in value) {
          return value.string;
        } else {
          return value;
        }
      })}
    </p>
  );
};
